/**
 * Simulação hidráulica no servidor (EPANET 2.2) e situação do modelo por setor — Nexora Grid (frontend, GUS-1105).
 *
 * Criado em: 17/10/2026 · Última modificação: 17/10/2026 · Versão: 1.0.0
 *
 * Mostra a última simulação de 24 h feita no servidor a partir do cadastro (tempos, NBR 12218 e pendências) e,
 * para engenharia, supervisor, coordenador ou suporte, o botão que gera o modelo e simula de novo. Setor com
 * modelo incompleto: a manobra do usuário sai com aviso registrado e a automação fica bloqueada.
 */
import { useEffect, useState } from "react";
import { api, ErroApi } from "../api";
import { pode } from "../permissoes";
import { useSessao } from "../sessao";

type Pendencia = { codigo: string; ativo: string; setor: string; mensagem: string };
type Hora = { hora: number; min: number; media: number; max: number; pct_nbr: number; abaixo_10: number };
type Ultima = {
  id: string; criado_em: string; autor: string; tempo_modelo_ms: number; tempo_simulacao_ms: number; motor: string;
  completo: boolean; pendencias: Pendencia[]; contagens: Record<string, number>; resumo: Record<string, { nos: number; horas: Hora[] }>;
};

export function SimulacaoServidor() {
  const { usuario, empresa } = useSessao();
  const [ultima, setUltima] = useState<Ultima | null>(null);
  const [estado, setEstado] = useState("");
  const [rodando, setRodando] = useState(false);
  const [abertas, setAbertas] = useState(false);

  const ler = () => {
    if (!empresa) return;
    api<Ultima>(`/v1/rede/simulacao?empresa=${empresa}`)
      .then((u) => { setUltima(u); setEstado(""); })
      .catch((e) => setEstado(e instanceof ErroApi && e.status === 404 ? "nenhuma simulação no servidor ainda" : "simulação no servidor indisponível"));
  };
  useEffect(ler, [empresa]);

  const simularAgora = async () => {
    setRodando(true);
    setEstado("gerando o modelo do cadastro e simulando 24 h no servidor…");
    try {
      await api(`/v1/rede/simulacao?empresa=${empresa}`, { metodo: "POST" });
      ler();
    } catch (e) {
      setEstado(e instanceof ErroApi ? e.message : "falha na simulação");
    } finally {
      setRodando(false);
    }
  };

  const geral = ultima?.resumo.geral?.horas ?? [];
  const pior = geral.length ? geral.reduce((a, b) => (b.pct_nbr < a.pct_nbr ? b : a)) : null;
  const setoresIncompletos = ultima ? [...new Set(ultima.pendencias.map((p) => p.setor).filter(Boolean))].sort() : [];

  return (
    <div className="simulacao-servidor" aria-live="polite">
      <h4>Simulação no servidor (EPANET)</h4>
      {ultima && (
        <>
          <p className="nx-suave">{new Date(ultima.criado_em).toLocaleString("pt-BR")} · {ultima.autor} · {ultima.contagens.nos} nós ·
            modelo {Math.round(ultima.tempo_modelo_ms)} ms + EPANET {Math.round(ultima.tempo_simulacao_ms)} ms</p>
          {pior && <p>Pior hora: <b className="nx-valor">{String(pior.hora).padStart(2, "0")}:00</b> — {pior.pct_nbr}% dos nós na faixa
            da NBR, mínima {pior.min} mca</p>}
          <p className={ultima.completo ? "" : "aviso-modelo"}>
            {ultima.completo ? "Modelo completo: automação liberada onde houver autorização do cliente."
              : `Modelo incompleto em ${setoresIncompletos.length} setor(es): manobra só pelo usuário, com aviso; automação bloqueada.`}
          </p>
          {ultima.pendencias.length > 0 && (
            <button type="button" className="nx-botao" onClick={() => setAbertas(!abertas)}>
              {abertas ? "ocultar" : "ver"} {ultima.pendencias.length} pendência(s)
            </button>
          )}
          {abertas && (
            <ul className="pendencias-modelo">
              {ultima.pendencias.slice(0, 60).map((p, i) => <li key={i}><b>{p.setor || "—"}</b> · {p.ativo || p.codigo} · <span className="nx-suave">{p.mensagem}</span></li>)}
              {ultima.pendencias.length > 60 && <li className="nx-suave">… e mais {ultima.pendencias.length - 60}</li>}
            </ul>
          )}
        </>
      )}
      {estado && <p className="nx-suave">{estado}</p>}
      {usuario && pode.simularNoServidor(usuario) && (
        <button type="button" className="nx-botao" disabled={rodando} onClick={simularAgora}>{rodando ? "simulando…" : "Simular no servidor"}</button>
      )}
    </div>
  );
}
